import { ReactNode } from "react";
import styled from "styled-components";
import { imgList } from "@constants/imgList";
import { device } from "@constants/styles";
import { useMyTranslation } from "@components/Lang";

interface IMetricDetailLayout {
  // header
  title: string; // e.g. t("detail.tempdetail")
  metricLabel: string; // active breadcrumb, e.g. t("detail.temp")
  devicesLabel: string; // first breadcrumb label — pass t("devices.head") ("รายการอุปกรณ์"), same as the device-detail page's breadcrumb
  onBack: () => void; // back arrow + breadcrumb device -> /device-detail/:id
  onBreadcrumbDevices: () => void; // breadcrumb "devices" -> /devices
  // headline card
  deviceName: string;
  image: string; // already-resolved <img src>
  locationName: string;
  soundSlot: ReactNode; // renderSound(...) output
  onSeeMore?: () => void; // opens device_detail modal; omit to hide the see-more button
  headerActions?: ReactNode; // page-level actions (Excel / PDF export) — top-right of the headline card
  breadcrumbActions?: ReactNode; // page-scope filters — right side of the breadcrumb row
  headlineExtra?: ReactNode; // optional full-width block INSIDE the headline card, below the device info (e.g. stat tiles + threshold banner)
  summarySlot?: ReactNode; // optional block (download actions) between the headline card and the chart
  // body slots (metric-specific, styled by the shared Container classes)
  filterBar: ReactNode; // <div className="detail__filter">...</div>
  chart?: ReactNode; // optional chart block, rendered between filter bar and table
  table: ReactNode; // <div className="detail__table"><Table/></div>
  modals?: ReactNode; // ModalDeviceDetail / ModalZoomImage / ModalFilter*
}

// Shared shell for the 4 per-metric detail pages (temperature / operation /
// power / iot). Owns the identical chrome (back button, breadcrumb, headline
// card) and all of the shared `detail__*` styling; each page supplies its own
// filter bar, table, and modals via slots.
export default function MetricDetailLayout({
  title,
  metricLabel,
  devicesLabel,
  onBack,
  onBreadcrumbDevices,
  deviceName,
  image,
  locationName,
  soundSlot,
  onSeeMore,
  headerActions,
  breadcrumbActions,
  headlineExtra,
  summarySlot,
  filterBar,
  chart,
  table,
  modals,
}: IMetricDetailLayout) {
  const { t } = useMyTranslation();
  return (
    <Container>
      <div className="detail__head">
        <div className="detail__header">
          <div className="detail__header-left">
            <div className="detail__top">
              <img src={imgList.ico_back} alt="" onClick={onBack} />
              <div className="font-inter size-30 color-blue828 weight-600">
                {title}
              </div>
            </div>
            <div className="detail__breadclumb">
              <div
                className="font-sukhumvitSet-text size-14 color-black pointer"
                onClick={onBreadcrumbDevices}
              >
                {devicesLabel}
              </div>
              <div className="sep">/</div>
              <div
                className="font-sukhumvitSet-text size-14 color-black pointer"
                onClick={onBack}
              >
                {deviceName}
              </div>
              <div className="sep">/</div>
              <div className="font-sukhumvitSet-text size-14 active">
                {metricLabel}
              </div>
            </div>
          </div>
          {breadcrumbActions ? (
            <div className="detail__header-actions">{breadcrumbActions}</div>
          ) : null}
        </div>
        <div className="detail__headline">
          <div className="detail__headline-main">
            <div className="detail__left">
              <div className="detail__cover">
                <img src={image} alt="" />
              </div>
              <div className="detail__left-detail">
                <div className="detail__row-head">
                  <div className="detail__name size-16 weight-600 font-sukhumvitSet-text color-black">
                    {deviceName}
                  </div>
                  {soundSlot}
                </div>
                <div className="detail__text detail__text--sp">
                  <img src={imgList.ico_location_gray} alt="" />
                  <div className="color-grayf4f size-12 font-sukhumvitSet-text location">
                    {locationName}
                  </div>
                </div>
              </div>
            </div>
            <div className="detail__right">
              {headerActions}
              {onSeeMore ? (
                <div className="detail__btn" onClick={onSeeMore}>
                  <div className="size-12 weight-600 font-sukhumvitSet-text color-black">
                    {t("detail.seemore")}
                  </div>
                  <img src={imgList.ico_arrow_yellow} alt="" />
                </div>
              ) : null}
            </div>
          </div>
          {headlineExtra}
        </div>
        {summarySlot}
        {/* The chart used to sit on the bare page background, which read as less
            important than the bordered summary/table blocks around it. Group the
            section heading (filterBar) and the chart into one card so the whole
            chart section is a single, defined object on the page. */}
        {chart ? (
          <ChartSection>
            {filterBar}
            {chart}
          </ChartSection>
        ) : (
          filterBar
        )}
        {table}
      </div>
      {modals}
    </Container>
  );
}

// Card around the chart section (heading + chart). White on white needs both a
// hairline border and a soft shadow to read; matches the `detail__card` look
// used on the device-detail page.
const ChartSection = styled.div`
  width: 100%;
  margin: 1.389vw 0 0;
  padding: 0 1.389vw 1.389vw;
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 1.111vw;
  box-shadow: 0px 0.251vw 1.255vw rgba(224, 224, 224, 0.42);
  @media ${device.desktopL} {
    margin: 20px 0 0;
    padding: 0 20px 20px;
    border-radius: 16px;
    box-shadow: 0px 3.61513px 18.0757px rgba(224, 224, 224, 0.42);
  }
  /* the heading brings its own top margin, which now doubles as the card's
     top padding — doubled ampersand to win over the Container's rule */
  && .detail__filter {
    margin: 1.111vw 0 0;
    @media ${device.desktopL} {
      margin: 16px 0 0;
    }
  }
`;

const Container = styled.div`
  width: cal(100% + 4.444vw);
  min-height: 100vh;
  margin: -2.222vw;
  background: #ffffff;
  @media ${device.desktopL} {
    width: cal(100% + 64px);
    margin: -32px;
  }
  .detail {
    &__head {
      border-radius: 2.778vw 0px 0px 0;
      padding: 1.111vw 2.222vw 1.389vw;
      background: #ffffff;
      @media ${device.desktopL} {
        border-radius: 40px 0px 0px 0;
        padding: 16px 32px 20px;
      }
    }
    &__top {
      width: 100%;
      display: flex;
      align-items: center;
      > img {
        width: 2.083vw;
        height: 2.083vw;
        margin: 0 0.833vw 0 0;
        cursor: pointer;
      }
      @media ${device.desktopL} {
        > img {
          width: 30px;
          height: 30px;
          margin: 0 12px 0 0;
        }
      }
    }
    /* title + breadcrumb as one tight left block; page-scope filters sit on the
       right, vertically centred against the whole block */
    &__header {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      row-gap: 0.833vw;
      /* no top margin: this is the first block inside detail__head, so a margin
         here would stack on top of that padding and push the title further down
         than on the device-detail page, which has no such wrapper */
      margin: 0;
      @media ${device.desktopL} {
        row-gap: 12px;
      }
    }
    &__header-actions {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.833vw;
      /* filters render compact here — the filter-bar sizing is too wide next
         to the breadcrumb block */
      & .detail__period {
        width: 13.889vw;
        margin: 0;
      }
      & .detail__filter-btn {
        height: 3.889vw;
        width: 3.889vw;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 0.069vw solid rgba(145, 158, 171, 0.32);
        border-radius: 0.556vw;
        cursor: pointer;
        > svg {
          width: 46%;
          height: 46%;
        }
      }
      @media ${device.desktopL} {
        gap: 12px;
        & .detail__period {
          width: 220px;
        }
        & .detail__filter-btn {
          height: 56px;
          width: 56px;
          border: 1px solid rgba(145, 158, 171, 0.32);
          border-radius: 8px;
        }
      }
      @media ${device.mobileLL} {
        width: 100%;
        & .detail__period {
          width: 100%;
        }
      }
    }
    &__breadclumb {
      display: flex;
      align-items: center;
      margin: 0.417vw 0 0;
      /* line up with the TITLE text, not the back arrow: the arrow (30px + 12px
         margin) indents the title, so an unindented breadcrumb left the two
         lines on different left edges */
      padding-left: 2.916vw;
      @media ${device.desktopL} {
        padding-left: 42px;
      }
      & .pointer {
        cursor: pointer;
      }
      & .sep {
        color: #919eab;
        margin: 0 0.556vw;
      }
      & .active {
        color: #919eab;
      }
      @media ${device.desktopL} {
        margin: 6px 0 0;
        & .sep {
          margin: 0 8px;
        }
      }
    }
    &__headline {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      margin: 1.389vw 0 0;
      padding: 1.389vw;
      border-radius: 1.111vw;
      border: 0.069vw solid #dedede;
      background: #fff;
      box-shadow: 0px 0.278vw 0.278vw 0px rgba(205, 205, 205, 0.25);
      @media ${device.desktopL} {
        margin: 20px 0 0;
        padding: 20px;
        border-radius: 16px;
        border: 1px solid #dedede;
        box-shadow: 0px 4px 12px 0px rgba(205, 205, 205, 0.25);
      }
    }
    &__headline-main {
      width: 100%;
      display: flex;
      align-items: center;
      @media ${device.mobileLL} {
        flex-wrap: wrap;
      }
    }
    &__cover {
      position: relative;
      width: 4.097vw;
      padding-top: 4.097vw;
      border-radius: 0.556vw;
      margin: 0 1.389vw 0 0;
      overflow: hidden;
      border-radius: 0.556vw;
      > img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        max-width: unset;
        max-height: unset;
        object-fit: cover;
      }
      @media ${device.desktopL} {
        width: 59px;
        padding-top: 59px;
        border-radius: 8px;
        margin: 0 20px 0 0;
        border-radius: 8px;
      }
    }
    &__row-head {
      width: 100%;
      display: flex;
      align-items: flex-start;
    }
    &__name {
      max-width: calc(100% - 1.736vw - 0.972vw);
      @media ${device.desktopL} {
        max-width: calc(100% - 25px - 14px);
      }
    }
    &__sound {
      width: 1.736vw;
      display: block;
      margin: 0 0 0 0.972vw;
      &--active {
        cursor: pointer;
      }
      @media ${device.desktopL} {
        width: 25px;
        margin: 0 0 0 14px;
      }
    }
    &__left {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: flex-start;
      @media ${device.mobileLL} {
        width: 100%;
      }
    }
    &__left-detail {
      width: calc(100% - 4.097vw- 1.389vw);
      @media ${device.desktopL} {
        width: calc(100% - 59px - 20px);
      }
    }
    &__right {
      flex: none;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.556vw;
      & .arrow {
        width: 1.389vw;
        height: 1.389vw;
        transform: rotate(270deg);
        margin: 0 0 0 0.139vw;
        cursor: pointer;
      }
      @media ${device.desktopL} {
        gap: 8px;
        & .arrow {
          width: 20px;
          height: 20px;
          margin: 0 0 0 2px;
        }
      }
      @media ${device.mobileLL} {
        width: 100%;
        align-items: flex-start;
      }
    }
    &__text {
      width: 48%;
      display: flex;
      align-items: flex-start;
      margin: 10px 0 0;
      > img {
        width: 1.111vw;
        margin: 0 0.417vw 0 0;
      }
      & .location {
      }
      &--sp {
        width: 100%;
      }
      & .current {
        margin: 0 0 0 0.417vw;
        cursor: pointer;
      }
      @media ${device.desktopL} {
        > img {
          width: 16px;
          margin: 0 6px 0 0;
        }
        & .location {
        }
        & .current {
          text-decoration: underline;
          margin: 0 0 0 6px;
          cursor: pointer;
        }
      }
    }
    &__filter {
      width: 100%;
      margin: 1.389vw 0 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      @media ${device.desktopL} {
        margin: 20px 0 0;
      }
      @media ${device.mobileLL} {
        flex-wrap: wrap;
      }
    }
    &__period {
      // width: calc((93% - 12.778vw) / 3);
      width: calc((93% - 18.056vw) / 3);
      margin: 0.694vw 0 0;
      /* Unit and period come from SelectSimple, which ships a 56px box, while
         the date beside them is DatePick's picker computing to 53 out of its
         padding. Three pixels, but the three sit in a row and it shows. Match
         the selects to the picker — scoped here rather than in SelectSimple,
         which the whole app uses. Same fix as the device-detail quick view. */
      & .ant-select-selector {
        height: 3.681vw !important;
      }
      @media ${device.desktopL} {
        // width: calc((93% - 184px) / 3);
        width: calc((93% - 260px) / 3);
        margin: 10px 0 0;
        & .ant-select-selector {
          height: 53px !important;
        }
      }
      @media ${device.mobileLL} {
        width: 100%;
        margin: 16px 0 0;
      }
    }
    &__download {
      width: 3.472vw;
      margin: 0.694vw 0 0;
      cursor: pointer;
      @media ${device.desktopL} {
        width: 50px;
        margin: 10px 0 0;
      }
    }
    &__head-2 {
      margin: 0.694vw 0 0;
      @media ${device.desktopL} {
        margin: 10px 0 0;
      }
    }
    &__table {
      margin: 1.389vw 0 0;
      & .ant-table-content {
        overflow: auto;
      }
      @media ${device.desktopL} {
        margin: 20px 0 0;
      }
    }
    &__btn {
      display: inline-flex;
      align-items: center;
      background: #ffffff;
      border: 0.139vw solid #ffe6a4;
      box-shadow: 0px 0.278vw 0.972vw rgba(165, 165, 165, 0.08);
      border-radius: 3.472vw;
      padding: 0.278vw 0.694vw;
      margin: 0.833vw 0px 0px;
      cursor: pointer;
      > div {
        line-height: 1.3;
        margin: 1px 0 0;
      }
      > img {
        width: 1.389vw;
        display: block;
        margin: 0 0 0 0.417vw;
      }
      @media ${device.desktopL} {
        border: 2px solid #ffe6a4;
        box-shadow: 0px 4px 14px rgba(165, 165, 165, 0.08);
        border-radius: 50px;
        padding: 4px 10px;
        margin: 12px 0px 0px;
        > div {
          margin: 1px 0 0;
        }
        > img {
          width: 20px;
          margin: 0 0 0 6px;
        }
      }
      @media ${device.mobileLL} {
        width: 100%;
        justify-content: center;
      }
    }
  }
`;
